/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
	ClientOauthAccountAuthenticationMethodsEnum,
	ClientPersonExtended
} from '@DigitaleDoerfer/digitale-doerfer-api';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ActionMenuItemProps } from '../../../../shared/views/ActionMenu/ActionMenu';
import {
	showSnackbar,
	SnackbarAction,
	SnackbarType
} from '../../../../shared/views/SnackbarNotification/store/SnackbarNotification.actions';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../../store/store';
import { UserActionTypes } from '../../store/UserActionTypes';
import ResetPasswordActionView from './ResetPasswordAction.view';
import { resetPasswordByAdmin } from '../../store/User.actions';

interface Props extends ActionMenuItemProps {
	user: ClientPersonExtended | null;
	showSnackbar: (snackbarAction: SnackbarAction) => void;
	resetPassword: (userId: string) => void;
}

class ResetPasswordActionContainer extends Component<Props> {
	constructor(props: Props) {
		super(props);

		this.handleSave = this.handleSave.bind(this);
	}

	handleSave(): void {
		const { user, resetPassword, showSnackbar } = this.props;
		const personId = user?.id;
		if (personId !== undefined) {
			resetPassword(personId);
		} else {
			showSnackbar({
				type: SnackbarType.SNACKBAR_ERROR,
				message: 'Die Person hat keine ID.'
			});
		}
	}

	render(): JSX.Element | null {
		const { renderMenuItem, user } = this.props;
		const authenticationMethods = user?.oauthAccount?.authenticationMethods;
		if (!authenticationMethods?.has(ClientOauthAccountAuthenticationMethodsEnum.UsernamePassword)) {
			return null;
		}
		return <ResetPasswordActionView renderMenuItem={renderMenuItem} handleSave={this.handleSave} />;
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		user: state.user.detail.user
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<UserActionTypes>) => {
	return {
		showSnackbar: (snackbarAction: SnackbarAction): Promise<void> => dispatch(showSnackbar(snackbarAction)),
		resetPassword: (userId: string): Promise<void> => dispatch(resetPasswordByAdmin(userId))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPasswordActionContainer);
